import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { FileText, CheckCircle2, Clock, XCircle, AlertCircle } from "lucide-react";
import {
  apiGetMyDocuments,
  apiUploadHRDocumentFile,
  type HRDocument,
} from "@/lib/api";
import { DocumentViewModal } from "@/components/documents/DocumentViewModal";
import { TableSkeleton } from "@/components/PageSkeleton";
import { toast } from "sonner";
import { format } from "date-fns";

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-12 flex flex-col items-center gap-2 text-center">
      <div className="h-11 w-11 rounded-xl bg-muted flex items-center justify-center">
        <FileText className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function StatusBadge({ status, t }: { status: string; t: (key: string, fallback: string) => string }) {
  switch (status) {
    case "Completed":
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1 pl-1.5">
          <CheckCircle2 className="w-3 h-3" />
          {t("documents.status.Completed", "Completed")}
        </Badge>
      );
    case "Signed":
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1 pl-1.5">
          <CheckCircle2 className="w-3 h-3" />
          {t("documents.status.Signed", "Signed")}
        </Badge>
      );
    case "Rejected":
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1 pl-1.5">
          <XCircle className="w-3 h-3" />
          {t("documents.status.Rejected", "Rejected")}
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1 pl-1.5">
          <Clock className="w-3 h-3" />
          {t("documents.status.Pending", "Pending")}
        </Badge>
      );
  }
}

export function MyDocuments() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewDoc, setViewDoc] = useState<HRDocument | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["my-documents"],
    queryFn: apiGetMyDocuments,
  });

  const handleDocumentUpdated = (updated: HRDocument) => {
    queryClient.setQueryData<HRDocument[]>(["my-documents"], (old = []) =>
      old.map((doc) => (doc.id === updated.id ? updated : doc)),
    );
    setViewDoc((prev) => (prev?.id === updated.id ? updated : prev));
    queryClient.invalidateQueries({ queryKey: ["pending-documents-count"] });
    if (updated.status !== "Pending") {
      queryClient.invalidateQueries({ queryKey: ["my-documents"] });
    }
  };

  useEffect(() => {
    const openId = searchParams.get("openDocument");
    if (openId && documents.length > 0) {
      const target = documents.find((doc) => doc.id === Number(openId));
      if (target) {
        setViewDoc(target);
        setViewModalOpen(true);
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("openDocument");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, documents, setSearchParams]);

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t("documents.myTitle")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("documents.subtitleSelf")}
              </p>
            </div>
          </div>
        </div>

        <Card className="shadow-card">
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-base font-semibold">
              {t("documents.assignedDocuments")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading && (
              <div className="p-4">
                <TableSkeleton rows={5} cols={5} />
              </div>
            )}

            {!isLoading && documents.length === 0 && (
              <EmptyState message={t("documents.empty.noAssignedDocuments")} />
            )}

            {!isLoading && documents.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("documents.documentName", "Document Name")}</TableHead>
                    <TableHead>{t("documents.typeLabel", "Type")}</TableHead>
                    <TableHead>{t("documents.requestedBy", "Requested By")}</TableHead>
                    <TableHead>{t("documents.dateLabel", "Date")}</TableHead>
                    <TableHead>{t("documents.statusLabel", "Status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => {
                    const isPending = doc.status === "Pending";
                    const isCompleted = doc.status === "Completed";
                    
                    let dateLabel = t("documents.requestedLabel", "Requested:");
                    const dateValue = doc.status === "Pending" ? doc.uploadedAt : doc.updatedAt;
                    if (doc.status === "Signed") {
                      dateLabel = t("documents.signedLabel", "Signed:");
                    } else if (isCompleted) {
                      dateLabel = t("documents.completedLabel", "Completed:");
                    }

                    return (
                      <TableRow 
                        key={doc.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setViewDoc(doc);
                          setViewModalOpen(true);
                        }}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <FileText className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium text-foreground">{doc.documentName}</span>
                            {isPending && !doc.fileUrl && (
                              <Badge variant="secondary" className="ml-2 bg-red-100 text-red-800 border-red-200">
                                {t("documents.actionRequired", "Action Required")}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {doc.documentType ? t(`documents.type.${doc.documentType}`, doc.documentType) : t("documents.type.Other", "Other")}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {doc.requestedByName || "HR"}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="text-muted-foreground mr-1">{dateLabel}</span>
                            <span className="font-medium">
                              {dateValue ? format(new Date(dateValue), "MMM d, yyyy") : "-"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={doc.status} t={t} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <DocumentViewModal
        doc={viewDoc}
        open={viewModalOpen}
        onOpenChange={(open) => {
          setViewModalOpen(open);
          if (!open) setViewDoc(null);
        }}
        canSign={true}
        isHRAdmin={false}
        onDocumentUpdated={handleDocumentUpdated}
      />
    </AppLayout>
  );
}

export default MyDocuments;
